import { assert, definedMap } from "@glideapps/ts-necessities";

import { SerializedComponent } from "../../components/SerializedApp";
import { makeText } from "../builder-utils";
import {
    AppDescriptionContext,
    ComponentBuilder,
    ComponentConfiguratorContext,
    PropertyDescriptor,
    WireActionHydrator,
    WireInflationBackend,
    WireValueGetter,
} from "../components";
import {
    makeSingleRelationOrThisItemPropertyDescriptor,
    makeTextPropertyDescriptor,
} from "../components/descriptor-utils";
import { PropertySection } from "../components/property-sections";
import { getEnumProperty } from "../components/property-utils";
import {
    ActionDescription,
    ActionKind,
    editClassScreenName,
    getTableName,
    getTableRefTableName,
    InputOutputTables,
    isSingleRelationType,
    MutatingScreenKind,
    PropertyDescription,
} from "../description";
import { titleProperties } from "../description-utils";
import { ActionDescriptor } from "./action-descriptor";
import { BaseActionHandler } from "./base";
import {
    getSingleRelationOrThis,
    getTableRefForSourceColumnOrThis,
    makeNavigationPropertyDescriptors,
    makeScreenContextForSourceColumn,
    navigationTargetModalPropertyHandler,
    SingleRelationOrThisDescription,
} from "./link-to-screen";
import { asMaybeString, isRow } from "../../computation-model/data";
import { TableName } from "../../database-strings";

interface PushEditScreenActionDescription extends ActionDescription, SingleRelationOrThisDescription {
    readonly kind: ActionKind.PushEditScreen;

    readonly title: PropertyDescription | undefined;
}

export class PushEditScreenHandler extends BaseActionHandler<PushEditScreenActionDescription> {
    public readonly kind = ActionKind.PushEditScreen;

    public readonly iconName = "share";

    public getDescriptor(
        _desc: PushEditScreenActionDescription | undefined,
        _tables: InputOutputTables | undefined,
        ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ActionDescriptor {
        const properties: PropertyDescriptor[] = [
            makeTextPropertyDescriptor("title", "Title", "Page title", true, mutatingScreenKind, {
                preferredNames: titleProperties,
                propertySection: PropertySection.Design,
                searchable: false,
            }),
            makeSingleRelationOrThisItemPropertyDescriptor("sourceColumn", "Data", PropertySection.Source),
            ...makeNavigationPropertyDescriptors(ccc, true),
        ];

        return { name: "Show Edit Screen", needsScreenContext: true, properties };
    }

    public getScreensUsed(
        desc: PushEditScreenActionDescription,
        adc: AppDescriptionContext,
        tables: InputOutputTables | undefined
    ): readonly string[] {
        if (tables === undefined) return [];

        const sourceColumn = getSingleRelationOrThis(desc.sourceColumn) ?? true;
        const tableRef = getTableRefForSourceColumnOrThis(adc, sourceColumn, tables, true);
        if (tableRef === undefined) return [];

        return [editClassScreenName(getTableRefTableName(tableRef))];
    }

    public makeAction(
        builder: ComponentBuilder,
        desc: PushEditScreenActionDescription,
        tables: InputOutputTables,
        containingScreenContext: SerializedComponent | undefined
    ): SerializedComponent | undefined {
        assert(containingScreenContext !== undefined);

        const title = definedMap(tables?.input, t =>
            makeText(builder, desc.title, t, true, true, false, false, containingScreenContext)
        );

        const sourceColumn = getSingleRelationOrThis(desc.sourceColumn) ?? true;
        const screenContext = makeScreenContextForSourceColumn(
            sourceColumn,
            builder,
            tables,
            containingScreenContext,
            true
        );
        if (screenContext === undefined) return undefined;

        return builder.makePushEditItemScreen(screenContext.contextTableType, screenContext.screenContext, title);
    }

    public inflate(ib: WireInflationBackend, desc: PushEditScreenActionDescription): WireActionHydrator | undefined {
        const [titleGetter] = ib.getValueGetterForProperty(desc.title, true);
        let tableName: TableName;
        let rowGetter: WireValueGetter;
        if (getEnumProperty(desc.sourceColumn) === true) {
            tableName = getTableName(ib.tables.input);
            rowGetter = vp => vp.rowContext?.inputRow;
        } else {
            const [getter, type] = ib.getValueGetterForProperty(desc.sourceColumn, false);
            if (!isSingleRelationType(type)) return undefined;
            const table = ib.findTable(type);
            if (table === undefined) return undefined;
            tableName = getTableName(table);
            rowGetter = getter;
        }
        const target = navigationTargetModalPropertyHandler.getEnum(desc);

        return vp => {
            const row = rowGetter(vp);
            if (!isRow(row)) return undefined;

            const title = asMaybeString(titleGetter(vp));

            return async ab => {
                ab.pushDefaultEditScreen(tableName, title, row, target);
                return true;
            };
        };
    }
}
